import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import type { AuthChangeEvent, Session } from '@supabase/supabase-js'

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [validToken, setValidToken] = useState(false)
  const [tokenChecked, setTokenChecked] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    // Handle auth state changes (important for password recovery)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('Auth state change:', event, session)
        if (event === 'PASSWORD_RECOVERY') {
          setValidToken(true)
          setTokenChecked(true)
          setError('')
        }
      }
    )

    // Check current session first
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session error:', error)
        setError('セッションの取得に失敗しました。')
        return
      }

      if (session?.user) {
        console.log('Existing session found:', session)
        setValidToken(true)
        setTokenChecked(true)
        setError('')
        return
      }
    })

    // Check if we have valid reset token parameters in URL
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    const type = searchParams.get('type')
    const tokenHash = searchParams.get('token_hash')

    console.log('URL Parameters:', {
      accessToken: accessToken ? 'present' : 'absent',
      refreshToken: refreshToken ? 'present' : 'absent',
      type,
      tokenHash: tokenHash ? 'present' : 'absent',
      allParams: Object.fromEntries(searchParams.entries())
    })

    if (accessToken && refreshToken && type === 'recovery') {
      // Set the session with the tokens from the URL
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(({ error }) => {
        if (error) {
          console.error('Invalid reset token:', error)
          setTokenChecked(true)
          setError('無効なリセットトークンです。新しいパスワードリセットリンクをリクエストしてください。')
        } else {
          setValidToken(true)
          setTokenChecked(true)
          setError('')
        }
      })
    } else if (tokenHash && type === 'recovery') {
      // Handle token_hash format - exchange for session
      supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery'
      }).then(({ error }) => {
        if (error) {
          console.error('Invalid reset token hash:', error)
          setTokenChecked(true)
          setError('無効なリセットトークンです。新しいパスワードリセットリンクをリクエストしてください。')
        } else {
          setValidToken(true)
          setTokenChecked(true)
          setError('')
        }
      })
    } else if (!accessToken && !refreshToken && !tokenHash) {
      // No tokens in URL - check if this might be a redirect from email
      console.log('No tokens in URL parameters')
      const timeoutId = setTimeout(() => {
        if (!validToken && !tokenChecked) {
          setTokenChecked(true)
          setError('無効なリセットリンクです。新しいパスワードリセットリンクをリクエストしてください。')
        }
      }, 2000) // Wait 2 seconds for auth state changes

      // Clear timeout if component unmounts or validToken changes
      return () => clearTimeout(timeoutId)
    }

    return () => {
      subscription.unsubscribe()
    }
  }, [searchParams, validToken, tokenChecked])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!password || !confirmPassword) {
      setError('すべての項目を入力してください')
      return
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません')
      return
    }

    // Validate password strength (same as PasswordChangeDialog)
    const passwordErrors = []

    if (password.length < 8) {
      passwordErrors.push('8文字以上である必要があります')
    }

    if (!/[A-Z]/.test(password)) {
      passwordErrors.push('大文字を含む必要があります')
    }

    if (!/[a-z]/.test(password)) {
      passwordErrors.push('小文字を含む必要があります')
    }

    if (!/[0-9]/.test(password)) {
      passwordErrors.push('数字を含む必要があります')
    }

    if (passwordErrors.length > 0) {
      setError(`パスワードの要件を満たしていません: ${passwordErrors.join(', ')}`)
      return
    }

    try {
      setIsLoading(true)
      setError('')

      // Get current user to ensure we have a valid session
      const { data: { user: currentUser }, error: getCurrentUserError } = await supabase.auth.getUser()
      if (getCurrentUserError || !currentUser) {
        throw new Error('ユーザー情報の取得に失敗しました')
      }

      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) {
        // Check if the error is about setting the same password
        if (error.message && error.message.includes('different from the old password')) {
          // Allow same password - just continue as if successful
          console.log('User attempted to set same password during reset - allowing this')
        } else {
          throw error
        }
      }

      // Wait a moment for the update to process
      await new Promise(resolve => setTimeout(resolve, 500))

      // Verify the session is still valid after the password update
      const { error: getUpdatedUserError } = await supabase.auth.getUser()

      if (getUpdatedUserError) {
        console.warn('Could not verify password update:', getUpdatedUserError)
        // Still proceed as the password update call didn't error
      }

      // Always show success for password reset, regardless of whether password changed
      // This handles the case where user sets the same password
      setSuccess(true)

      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/?login=true')
      }, 3000)

    } catch (error: unknown) {
      console.error('Password reset failed:', error)
      setError('パスワードの更新に失敗しました。もう一度お試しください。')
    } finally {
      setIsLoading(false)
    }
  }

  if (!validToken && !tokenChecked) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">トークンを確認中...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">新しいパスワードを設定</h1>
            <p className="text-gray-600 mt-2">
              新しいパスワードを入力してください
            </p>
          </div>

          {error && !validToken && tokenChecked ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-blue-600 hover:text-blue-500 text-sm"
                >
                  新しいパスワードリセットリンクをリクエスト
                </Link>
              </div>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="text-green-800">
                  <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  パスワードが正常に更新されました！
                </div>
              </div>
              <p className="text-sm text-gray-600">
                3秒後にログインページにリダイレクトします...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  新しいパスワード
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="新しいパスワードを入力"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  パスワード確認
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="パスワードを再入力"
                  required
                  minLength={8}
                />
              </div>

              {password && password.length < 8 && (
                <div className="text-sm text-gray-600">
                  パスワードは8文字以上で入力してください
                </div>
              )}

              {password && confirmPassword && password !== confirmPassword && (
                <div className="text-sm text-red-600">
                  パスワードが一致しません
                </div>
              )}

              <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                <strong>パスワード要件:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  <li>8文字以上</li>
                  <li>大文字を含む</li>
                  <li>小文字を含む</li>
                  <li>数字を含む</li>
                </ul>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-red-800 text-sm">{error}</div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || password !== confirmPassword || password.length < 8}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? 'パスワード更新中...' : 'パスワードを更新'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/?login=true"
              className="text-blue-600 hover:text-blue-500 text-sm"
            >
              ログインページに戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage